if (!customElements.get('product-buy')) {
  customElements.define(
    'product-buy',
    class ProductBuy extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-product-buy-data]');
        this.form = this.querySelector('.product-buy__form');
        if (!dataEl || !this.form) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (error) {
          return;
        }

        this.quantityInput = this.form.querySelector('[data-quantity]');
        this.planInput = this.form.querySelector('[data-selling-plan]');
        this.submitButton = this.form.querySelector('[data-submit]');
        this.submitLabel = this.form.querySelector('[data-submit-label]');
        this.buyNowButton = this.form.querySelector('[data-buy-now]');
        this.errorEl = this.form.querySelector('[data-error]');
        this.packButtons = [...this.querySelectorAll('[data-pack-index]')];
        this.purchaseInputs = [...this.querySelectorAll('[data-purchase-type]')];
        this.mainStage = this.querySelector('.product-buy__main');
        this.mediaItems = [...this.querySelectorAll('[data-media-item]')];
        this.videos = [...this.querySelectorAll('.product-buy__main-video')];
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];

        this.packIndex = Math.min(this.data.defaultIndex || 0, Math.max(this.data.packs.length - 1, 0));
        const checked = this.purchaseInputs.find((input) => input.checked);
        this.purchaseType = checked ? checked.value : 'once';

        this.onPurchaseChange = this.onPurchaseChange.bind(this);
        this.onPackClick = this.onPackClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onBuyNow = this.onBuyNow.bind(this);
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onPageShow = this.onPageShow.bind(this);

        this.purchaseInputs.forEach((input) => input.addEventListener('change', this.onPurchaseChange));
        this.packButtons.forEach((button) => button.addEventListener('click', this.onPackClick));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
        this.form.addEventListener('submit', this.onSubmit);
        this.buyNowButton?.addEventListener('click', this.onBuyNow);
        window.addEventListener('pageshow', this.onPageShow);

        this.render();
        requestAnimationFrame(() => this.setAttribute('data-ready', ''));

        if (this.videos.length > 0) {
          this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.onVideoPause = (event) => {
            if (this.shouldPlay(event.target) && !document.hidden) this.playVideo(event.target);
          };
          this.onGesture = this.onGesture.bind(this);
          this.videos.forEach((video) => {
            video.muted = true;
            video.pause();
            video.addEventListener('pause', this.onVideoPause);
          });
          this.videoObserver = new IntersectionObserver(([entry]) => {
            this.videoVisible = entry.isIntersecting;
            this.syncVideos();
          }, { rootMargin: '100% 0px' });
          this.videoObserver.observe(this.mainStage);
        }
      }

      disconnectedCallback() {
        this.purchaseInputs?.forEach((input) => input.removeEventListener('change', this.onPurchaseChange));
        this.packButtons?.forEach((button) => button.removeEventListener('click', this.onPackClick));
        this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
        this.form?.removeEventListener('submit', this.onSubmit);
        this.buyNowButton?.removeEventListener('click', this.onBuyNow);
        window.removeEventListener('pageshow', this.onPageShow);
        this.checkoutForm?.remove();
        this.videoObserver?.disconnect();
        this.videos?.forEach((video) => video.removeEventListener('pause', this.onVideoPause));
        this.stopWaitingForGesture();
      }

      get pack() {
        return this.data.packs[this.packIndex];
      }

      get isSubscription() {
        return this.purchaseType === 'subscribe' && Boolean(this.pack);
      }

      onPurchaseChange(event) {
        this.purchaseType = event.target.value;
        this.render();
      }

      onPackClick(event) {
        this.packIndex = Number(event.currentTarget.dataset.packIndex) || 0;
        if (this.purchaseType !== 'subscribe') {
          this.purchaseType = 'subscribe';
          const input = this.purchaseInputs.find((item) => item.value === 'subscribe');
          if (input) input.checked = true;
        }
        this.render();
      }

      onThumbClick(event) {
        const thumb = event.currentTarget;
        const hasMainVideoThumb = this.thumbs.some((item) => item.dataset.thumb === 'main-video');
        const hasMainVideo = this.mediaItems.some((item) => item.dataset.mediaItem === 'main-video');
        if (hasMainVideo && !hasMainVideoThumb && thumb.classList.contains('is-active')) {
          this.showMedia('main-video');
          return;
        }
        this.showMedia(thumb.dataset.thumb);
      }

      showMedia(id) {
        if (!this.mediaItems.some((item) => item.dataset.mediaItem === id)) return;
        this.mediaItems.forEach((item) => {
          item.hidden = item.dataset.mediaItem !== id;
        });
        this.thumbs.forEach((item) => {
          const active = item.dataset.thumb === id;
          item.classList.toggle('is-active', active);
          if (active) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });
        this.syncVideos();
      }

      shouldPlay(video) {
        return this.videoVisible && !this.reducedMotion && !video.closest('[data-media-item]')?.hidden;
      }

      syncVideos() {
        this.videos.forEach((video) => {
          if (this.shouldPlay(video)) {
            this.playVideo(video);
          } else {
            video.pause();
          }
        });
      }

      playVideo(video) {
        video.play().catch((error) => {
          if (error?.name === 'NotAllowedError') this.waitForGesture();
        });
      }

      waitForGesture() {
        if (this.waitingForGesture) return;
        this.waitingForGesture = true;
        document.addEventListener('touchend', this.onGesture, { passive: true, capture: true });
        document.addEventListener('click', this.onGesture, { capture: true });
      }

      stopWaitingForGesture() {
        if (!this.waitingForGesture) return;
        this.waitingForGesture = false;
        document.removeEventListener('touchend', this.onGesture, { capture: true });
        document.removeEventListener('click', this.onGesture, { capture: true });
      }

      onGesture() {
        this.stopWaitingForGesture();
        this.syncVideos();
      }

      render() {
        const subscribe = this.isSubscription;
        const pack = this.pack;

        this.querySelectorAll('[data-option]').forEach((option) => {
          option.classList.toggle('is-selected', option.dataset.option === (subscribe ? 'subscribe' : 'once'));
        });

        this.packButtons.forEach((button, index) => {
          const active = index === this.packIndex;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-checked', active ? 'true' : 'false');
        });

        if (pack) {
          this.setText('[data-sub-total]', pack.total);
          this.setText('[data-sub-per-can]', pack.perCan);
          this.setText('[data-sub-save]', pack.save);
          this.setText('[data-detail-title]', pack.title);
          this.setText('[data-detail-note]', pack.note);
          this.querySelectorAll('[data-pack-image]').forEach((image) => {
            image.hidden = Number(image.dataset.packImage) !== this.packIndex;
          });
          this.querySelectorAll('[data-detail-popular]').forEach((tag) => {
            tag.classList.toggle('is-collapsed', !(pack.popular && subscribe));
          });
          this.querySelectorAll('[data-subscribe-popular]').forEach((tag) => {
            tag.classList.toggle('is-collapsed', !(pack.popular && subscribe));
          });
          this.renderPerks(pack);
        }

        const collapse = this.querySelector('.product-buy__collapse');
        if (collapse) collapse.inert = !subscribe;

        const price = this.querySelector('[data-price]');
        const compareRow = this.querySelector('[data-compare-row]');
        if (price) price.textContent = subscribe ? pack.total : this.data.oneTime.price;
        if (compareRow) {
          compareRow.hidden = !subscribe;
          if (subscribe) {
            this.setText('[data-compare]', pack.compare);
            this.setText('[data-off]', pack.off);
          }
        }

        this.quantityInput.value = subscribe ? pack.quantity : 1;
        this.planInput.value = subscribe ? pack.plan : '';
        this.planInput.disabled = !subscribe;
        if (this.submitLabel) this.submitLabel.textContent = subscribe ? pack.cta : this.data.oneTime.cta;
      }

      renderPerks(pack) {
        const perks = [...this.querySelectorAll('[data-perk-pack]')];
        let visibleCount = 0;
        perks.forEach((perk) => {
          const target = perk.dataset.perkPack;
          const visible = target === 'all' || target === String(pack.quantity);
          perk.hidden = !visible;
          if (visible) visibleCount += 1;
        });
        this.querySelectorAll('[data-perks-part]').forEach((part) => {
          part.hidden = visibleCount === 0;
        });
      }

      setText(selector, value) {
        this.querySelectorAll(selector).forEach((el) => {
          el.textContent = value;
        });
      }

      async addToCart() {
        this.setError('');
        const body = new FormData(this.form);
        if (!this.isSubscription) body.delete('selling_plan');
        const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || '');
        try {
          const cart = await (await fetch('/cart.js')).json();
          if (typeof window.updateCartCount === 'function') window.updateCartCount(cart);
        } catch (error) {
          return result;
        }
        return result;
      }

      async onSubmit(event) {
        event.preventDefault();
        this.setBusy(true, this.submitButton);
        try {
          await this.addToCart();
        } catch (error) {
          this.setError(error.message || this.data.errorMessage);
        } finally {
          this.setBusy(false);
        }
      }

      onBuyNow() {
        this.setError('');
        this.setBusy(true, this.buyNowButton);
        const root = window.Shopify?.routes?.root || '/';
        const data = new FormData(this.form);
        if (!this.isSubscription) data.delete('selling_plan');
        data.set('return_to', `${root}checkout`);

        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = `${root}cart/add`;
        checkoutForm.hidden = true;
        data.forEach((value, name) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          checkoutForm.appendChild(input);
        });
        this.checkoutForm?.remove();
        this.checkoutForm = checkoutForm;
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();
      }

      onPageShow(event) {
        if (!event.persisted || !this.checkoutForm) return;
        this.checkoutForm.remove();
        this.checkoutForm = null;
        this.setBusy(false);
      }

      setBusy(busy, trigger) {
        [this.submitButton, this.buyNowButton].forEach((button) => {
          if (!button) return;
          button.disabled = busy;
          button.setAttribute('aria-busy', busy && (!trigger || button === trigger) ? 'true' : 'false');
        });
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
    }
  );
}
